import type { PaymentReview } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { dashboardApi } from '../../lib/apiClient';
import { readSlipImage } from './slipImage';

const money = (amount: number) =>
  new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(
    amount / 100,
  );
const time = (value: string | null) =>
  value
    ? new Date(value).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' })
    : '—';
const RESULT: Record<string, string> = {
  verified: 'ตรวจผ่านและจับคู่รายการแล้ว',
  previously_verified: 'สลิปนี้ตรวจผ่านในบิลนี้แล้ว',
  checking: 'กำลังตรวจสอบ',
  reserved: 'กำลังยืนยันรายการ',
  pending: 'รอตรวจสอบ',
  biller_mismatch: 'ผู้รับเงินไม่ตรงกับร้าน',
  amount_mismatch: 'ยอดเงินไม่ตรง',
  reference_mismatch: 'เลขอ้างอิงไม่ตรงกับบิล',
  transaction_mismatch: 'รายการธนาคารไม่ตรงกับสลิป',
  payment_reversed: 'รายการนี้ถูกยกเลิกการชำระแล้ว',
  slip_already_used: 'สลิปนี้ถูกใช้ยืนยันรายการอื่นแล้ว',
  slip_unverified: 'ธนาคารยังไม่ยืนยันสลิป',
  inquiry_unavailable: 'ยังสอบถามสถานะธนาคารไม่ได้',
  provider_unavailable: 'ติดต่อธนาคารไม่ได้ กรุณาตรวจสอบอีกครั้ง',
  verification_unavailable: 'ยังยืนยันบิลไม่ได้ กรุณาตรวจสอบอีกครั้ง',
  payment_requires_review: 'รายการต้องตรวจสอบเพิ่มเติม',
  void_not_yet_confirmed: 'ธนาคารยังไม่ยืนยันการยกเลิก',
  inquiry_state_conflict: 'สถานะธนาคารยังไม่ตรงกัน',
};
const status = (payment: PaymentReview) =>
  payment.voidStatus === 'succeeded'
    ? 'ยกเลิกชำระแล้ว'
    : payment.voidStatus !== 'none'
      ? 'รอตรวจสอบผล Void'
      : payment.reviewStatus === 'released'
        ? 'คืนรายการให้ POS แล้ว'
        : payment.reviewStatus === 'confirmed'
          ? 'ยืนยันชำระแล้ว'
          : payment.reviewStatus === 'attention'
            ? 'ต้องตรวจสอบเพิ่มเติม'
            : 'รอแนบสลิป / ตรวจสอบ';

export function PaymentReviewQueue({
  branchId,
  onSelect,
}: {
  branchId: string;
  onSelect: (paymentId: string) => void;
}) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [offset, setOffset] = useState(0);
  useEffect(() => setOffset(0), [branchId]);
  const query = useQuery({
    queryKey: ['payment-review-queue', branchId, showCompleted, offset],
    queryFn: () =>
      dashboardApi.listPaymentReviews(branchId, showCompleted, offset),
    enabled: !!branchId,
    refetchInterval: 15000,
  });
  const isComplete = (p: PaymentReview) =>
    p.reviewStatus === 'confirmed' ||
    p.reviewStatus === 'released' ||
    p.voidStatus === 'succeeded';
  const all = query.data?.items ?? [];
  const items = all.filter((p) => showCompleted || !isComplete(p));
  return (
    <section
      className="mb-6 rounded-xl border border-line bg-white p-4"
      aria-label="คิวลูกค้าแจ้งโอนแล้ว"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-semibold">ลูกค้าแจ้งโอนแล้ว</h2>
          <p className="text-sm text-ink-3">
            แนบสลิปเพื่อตรวจสอบและจับคู่กับบิลจาก POS · รอตรวจ{' '}
            {query.data?.openCount ?? 0} รายการ
          </p>
        </div>
        <label className="text-sm">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => {
              setShowCompleted(e.target.checked);
              setOffset(0);
            }}
          />{' '}
          แสดงรายการที่ดำเนินการแล้ว
        </label>
      </div>
      {query.isError ? (
        <p role="alert">
          โหลดคิวไม่สำเร็จ{' '}
          <button onClick={() => void query.refetch()}>ลองใหม่</button>
        </p>
      ) : query.isLoading ? (
        <p>กำลังโหลดคิว…</p>
      ) : items.length === 0 ? (
        <p className="text-ink-3">ไม่มีรายการรอตรวจสอบ</p>
      ) : (
        <div className="divide-y divide-line">
          {items.map((payment) => (
            <button
              key={payment.paymentId}
              className="flex w-full items-center justify-between gap-4 py-3 text-left"
              onClick={() => onSelect(payment.paymentId)}
            >
              <span>
                <span className="block font-medium break-all">
                  {payment.reference}
                </span>
                <span className="text-sm text-ink-3">
                  {time(payment.reportedAt)} · {status(payment)}
                </span>
              </span>
              <strong className="whitespace-nowrap">
                {money(payment.amountSatang)}
              </strong>
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-3">
        {offset > 0 && (
          <Btn onClick={() => setOffset(Math.max(0, offset - 200))}>
            หน้าก่อนหน้า
          </Btn>
        )}
        {query.data?.nextOffset != null && (
          <Btn onClick={() => setOffset(query.data!.nextOffset!)}>
            หน้าถัดไป
          </Btn>
        )}
      </div>
    </section>
  );
}

export function PaymentOperationsPanel({ paymentId }: { paymentId: string }) {
  const client = useQueryClient();
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [reason, setReason] = useState('');
  const [releaseReason, setReleaseReason] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['payment-operation', paymentId],
    queryFn: () => dashboardApi.getPaymentReview(paymentId),
    refetchInterval: 15000,
  });
  const refreshed = async () => {
    await Promise.all([
      query.refetch(),
      client.invalidateQueries({ queryKey: ['payment-review-queue'] }),
      client.invalidateQueries({ queryKey: ['payment-reconciliation'] }),
      client.invalidateQueries({ queryKey: ['payment-reconciliation-detail'] }),
    ]);
  };
  const release = useMutation({
    mutationFn: () =>
      dashboardApi.releasePaymentReview(paymentId, releaseReason.trim()),
    onSuccess: refreshed,
  });
  const resumeVoid = useMutation({
    mutationFn: () =>
      dashboardApi.voidScbPayment(paymentId, query.data!.payment.voidReason!),
    onSuccess: refreshed,
  });
  const upload = useMutation({
    mutationFn: async (file: File) =>
      dashboardApi.attachPaymentSlip(paymentId, await readSlipImage(file)),
    onSuccess: refreshed,
  });
  const verify = useMutation({
    mutationFn: (id: string) => dashboardApi.verifyPaymentSlip(paymentId, id),
    onSuccess: refreshed,
  });
  const cancel = useMutation({
    mutationFn: () => dashboardApi.voidScbPayment(paymentId, reason.trim()),
    onSuccess: async () => {
      setConfirmVoid(false);
      await refreshed();
    },
  });
  const check = useMutation({
    mutationFn: () => dashboardApi.checkScbVoid(paymentId),
    onSuccess: refreshed,
  });
  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl],
  );
  useEffect(() => {
    setImageUrl(null);
    setConfirmVoid(false);
    setReason('');
  }, [paymentId]);
  const showImage = async (id: string) => {
    try {
      setImageError(null);
      setImageUrl(
        URL.createObjectURL(
          await dashboardApi.getPaymentSlipImage(paymentId, id),
        ),
      );
    } catch {
      setImageError('โหลดภาพหลักฐานไม่สำเร็จ');
    }
  };
  if (query.isLoading) return <p>กำลังโหลดหลักฐานการชำระ…</p>;
  if (query.isError || !query.data)
    return (
      <p role="alert">
        โหลดข้อมูลตรวจสอบสลิปและ Void ไม่สำเร็จ{' '}
        <button onClick={() => void query.refetch()}>ลองใหม่</button>
      </p>
    );
  const { payment, evidence } = query.data;
  const busy =
    upload.isPending ||
    verify.isPending ||
    cancel.isPending ||
    check.isPending ||
    release.isPending ||
    resumeVoid.isPending;
  const error =
    upload.error ||
    verify.error ||
    cancel.error ||
    check.error ||
    release.error ||
    resumeVoid.error;
  return (
    <section
      className="space-y-4 rounded-lg border border-line p-4"
      aria-label="ตรวจสอบสลิปและ Void"
    >
      <div>
        <h3 className="font-semibold">ตรวจสอบสลิปและยกเลิกการชำระ</h3>
        <p role="status">{status(payment)}</p>
        {payment.reportedAt && (
          <p className="text-sm text-ink-3">
            POS แจ้งโอนแล้วเมื่อ {time(payment.reportedAt)}
          </p>
        )}
        {payment.lastResult && (
          <p className="text-sm">
            {RESULT[payment.lastResult] ?? 'ต้องตรวจสอบรายการเพิ่มเติม'}
          </p>
        )}
      </div>
      {payment.reportedAt &&
        payment.reviewStatus !== 'released' &&
        payment.voidStatus === 'none' &&
        !['confirmed', 'late_confirmed'].includes(payment.paymentStatus) && (
          <div className="space-y-2 border-t pt-3">
            <p>
              หากแจ้งโอนผิดหรือไม่มีการชำระ ให้คืนรายการไปเก็บเงินต่อที่ POS
              ระบบจะสอบถามธนาคารก่อนคืนรายการ
            </p>
            <label>
              เหตุผลคืนรายการ
              <input
                className="block w-full border rounded p-2"
                value={releaseReason}
                maxLength={500}
                onChange={(e) => setReleaseReason(e.target.value)}
              />
            </label>
            <Btn
              disabled={busy || releaseReason.trim().length < 3}
              onClick={() => release.mutate()}
            >
              คืนรายการให้ POS
            </Btn>
          </div>
        )}
      {payment.reviewStatus === 'released' && (
        <p>
          คืนรายการแล้ว: {payment.releaseReason} · เปิดรายการค้างที่ POS
          เพื่อเลือกวิธีชำระใหม่
        </p>
      )}
      {payment.voidStatus === 'none' && (
        <label className="block text-sm font-medium">
          แนบสลิปเพื่อตรวจสอบ (JPG / PNG ไม่เกิน 2 MB)
          <input
            className="mt-2 block w-full"
            type="file"
            accept="image/jpeg,image/png"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <span className="block mt-1 text-ink-3">
            ระบบอ่าน Mini QR ตรวจธนาคาร และยืนยันบิลเมื่อข้อมูลตรงครบ
          </span>
        </label>
      )}
      {upload.isPending && (
        <p role="status">กำลังอ่านสลิปและตรวจสอบกับธนาคาร…</p>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {upload.error instanceof Error &&
          !(upload.error.name === 'DashboardApiError')
            ? upload.error.message
            : 'ดำเนินการไม่สำเร็จ กรุณาตรวจสอบสถานะก่อนลองอีกครั้ง'}
        </p>
      )}
      {evidence.map((item) => (
        <div
          key={item.evidenceId}
          className="rounded border border-line p-3 text-sm"
        >
          <p>
            {time(item.uploadedAt)} ·{' '}
            {RESULT[item.result] ?? 'รอตรวจสอบเพิ่มเติม'}
          </p>
          <p className="break-all">เลขอ้างอิงสลิป {item.transRef}</p>
          <div className="mt-2 flex gap-2">
            <Btn onClick={() => void showImage(item.evidenceId)}>ดูสลิป</Btn>
            {payment.voidStatus === 'none' &&
              !['verified', 'previously_verified'].includes(item.result) && (
                <Btn
                  disabled={busy}
                  onClick={() => verify.mutate(item.evidenceId)}
                >
                  ตรวจสอบอีกครั้ง
                </Btn>
              )}
          </div>
        </div>
      ))}
      {imageError && <p role="alert">{imageError}</p>}
      {imageUrl && (
        <div>
          <button onClick={() => setImageUrl(null)}>ปิดภาพ</button>
          <img
            src={imageUrl}
            alt="หลักฐานสลิปการชำระเงิน"
            className="max-h-[600px] max-w-full object-contain"
          />
        </div>
      )}
      {payment.voidStatus === 'succeeded' ? (
        <div className="rounded bg-amber-50 p-3 text-sm">
          <p>
            SCB ยืนยันยกเลิกการชำระ {money(payment.amountSatang)} เมื่อ{' '}
            {time(payment.voidedAt)}
          </p>
          <p>
            {payment.deviceAppliedAt
              ? 'POS รับผลแล้ว'
              : 'รอ POS ออนไลน์เพื่อรับผล'}
          </p>
          <p>เหตุผล: {payment.voidReason}</p>
          <p>
            ประวัติการขายยังคงอยู่ กรุณาติดตามยอดชำระหรือการคืนสินค้าของบิลนี้
          </p>
        </div>
      ) : payment.voidStatus !== 'none' ? (
        <div className="space-y-2">
          <p>
            {payment.voidSubmittedAt
              ? 'กำลังติดตามผลการยกเลิกจากธนาคาร'
              : 'ยังไม่ได้ส่งคำขอ Void ไปธนาคาร'}
          </p>
          {!payment.voidSubmittedAt && (
            <Btn disabled={busy} onClick={() => resumeVoid.mutate()}>
              ส่งคำขอ Void ต่อ
            </Btn>
          )}
          <Btn disabled={busy} onClick={() => check.mutate()}>
            ตรวจสอบผล Void
          </Btn>
        </div>
      ) : (
        ['confirmed', 'late_confirmed'].includes(payment.paymentStatus) && (
          <div className="border-t border-line pt-3">
            {!confirmVoid ? (
              <Btn disabled={busy} onClick={() => setConfirmVoid(true)}>
                ยกเลิกการชำระผ่าน SCB
              </Btn>
            ) : (
              <div className="space-y-3 rounded bg-amber-50 p-3">
                <p className="font-semibold">
                  ยืนยันยกเลิกการชำระ {money(payment.amountSatang)}
                </p>
                <p className="text-sm break-all">
                  เลขอ้างอิง {payment.reference}
                </p>
                <p className="text-sm">
                  ยกเลิกเฉพาะเงินที่รับผ่าน SCB ประวัติการขายและสต็อกยังคงอยู่
                </p>
                <label className="block text-sm">
                  เหตุผล
                  <input
                    className="mt-1 block w-full rounded border p-2"
                    value={reason}
                    maxLength={500}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div className="flex gap-2">
                  <Btn
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => cancel.mutate()}
                  >
                    {cancel.isPending ? 'กำลังส่งคำขอ…' : 'ยืนยัน Void'}
                  </Btn>
                  <Btn disabled={busy} onClick={() => setConfirmVoid(false)}>
                    กลับ
                  </Btn>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
}
